import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import {
  CheckCircle2, XCircle, RefreshCw, Clock, ChefHat,
  Loader2, Sparkles, Star, Trophy, RotateCcw, Users, Check, X
} from 'lucide-react';
import { formatDateFR, todayZurich } from '../utils/dateFormat';
import MyKitchenTasks from '../components/tasks/MyKitchenTasks';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SwipeTaskCard = ({ task, onSwipeRight, onSwipeLeft, isTop }) => {
  const [exitDir, setExitDir] = useState(null);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const doneOpacity = useTransform(x, [0, 100], [0, 1]);
  const skipOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 100) { setExitDir('right'); onSwipeRight(task); }
    else if (info.offset.x < -100) { setExitDir('left'); onSwipeLeft(task); }
  };

  const assignees = task.assigned_to_names?.join(', ') || '';

  return (
    <motion.div
      data-testid={`swipe-card-${task.id}`}
      className={`absolute w-full max-w-md ${isTop ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ x, rotate }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      initial={{ scale: isTop ? 1 : 0.95, y: isTop ? 0 : 10, opacity: isTop ? 1 : 0.7 }}
      animate={{ scale: isTop ? 1 : 0.95, y: isTop ? 0 : 10, opacity: isTop ? 1 : 0.7 }}
      exit={{ x: exitDir === 'right' ? 300 : exitDir === 'left' ? -300 : 0, opacity: 0, transition: { duration: 0.3 } }}
      whileTap={isTop ? { scale: 1.02 } : {}}
    >
      <div className="bg-white border border-gray-200 rounded-3xl shadow-card-stack overflow-hidden">
        {/* Header gradient */}
        <div className="h-3 bg-gradient-to-r from-neon-pink via-neon-yellow to-neon-pink" />

        {/* Swipe indicators */}
        <motion.div className="absolute top-6 right-4 bg-neon-pink text-gray-900 px-4 py-2 rounded-full font-bold text-lg transform rotate-12 shadow-neon-pink z-10" style={{ opacity: doneOpacity }}>
          FAIT !
        </motion.div>
        <motion.div className="absolute top-6 left-4 bg-red-500 text-gray-900 px-4 py-2 rounded-full font-bold text-lg transform -rotate-12 z-10" style={{ opacity: skipOpacity }}>
          PASSER
        </motion.div>

        <div className="p-6">
          {/* Badges */}
          <div className="flex items-center gap-2 mb-3">
            {task.is_mine ? (
              <Badge className="bg-neon-pink/10 text-neon-pink border border-neon-pink/30">Ma tâche</Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 border border-amber-300 flex items-center gap-1">
                <Star size={12} />Bonus
              </Badge>
            )}
            <Badge className="bg-gray-100 text-gray-500 border border-gray-200">{task.station?.replace('Poste ', '') || ''}</Badge>
            {task.status === 'skipped' && (
              <Badge className="bg-orange-500/10 text-orange-600 border border-orange-500/30 flex items-center gap-1">
                <RotateCcw size={12} />Reprise
              </Badge>
            )}
          </div>

          <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">{task.item_name}</h3>

          {task.note && (
            <div className="bg-neon-yellow/5 border border-neon-yellow/20 rounded-xl p-3 mb-3">
              <p className="text-sm text-amber-700">{task.note}</p>
            </div>
          )}

          <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
            {task.quantity > 0 && (
              <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1 rounded-full">
                <ChefHat size={14} strokeWidth={2.5} />
                <span className="font-medium">{task.quantity} {task.unit}</span>
              </div>
            )}
            {assignees && (
              <div className="flex items-center gap-1.5">
                <Users size={14} />
                <span>{assignees}</span>
              </div>
            )}
          </div>

          <div className="flex justify-center gap-8 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-red-600 font-medium">
              <X size={20} strokeWidth={3} />
              <span className="text-sm">Passer</span>
            </div>
            <div className="flex items-center gap-2 text-neon-pink font-medium">
              <span className="text-sm">Terminé</span>
              <Check size={20} strokeWidth={3} />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_manager;
  const [tasks, setTasks] = useState([]);
  const [doneCount, setDoneCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [flashType, setFlashType] = useState(null);

  const today = todayZurich();
  const [tasksDate, setTasksDate] = useState(today);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      // Get swipe tasks (backend filters by role: admin sees all, member sees assigned only)
      const res = await axios.get(`${API}/prep/swipe`, { params: { date: today } });
      setTasks(res.data.data || []);

      // Get done count for today
      const doneRes = await axios.get(`${API}/prep/daily`, { params: { date: today } });
      const allDaily = (doneRes.data.data || []).filter(d => d.status !== 'replaced');

      if (isAdmin) {
        setDoneCount(allDaily.filter(t => t.status === 'done').length);
        setSkippedCount(allDaily.filter(t => t.status === 'skipped').length);
        setTotalAssigned(allDaily.length);
      } else {
        // Member: count only their tasks
        const memberId = user?.member_id || '';
        const userName = user?.name || '';
        const myDaily = allDaily.filter(t =>
          (t.assigned_to || []).includes(memberId) || (t.assigned_to_names || []).includes(userName)
        );
        setDoneCount(myDaily.filter(t => t.status === 'done').length);
        setSkippedCount(myDaily.filter(t => t.status === 'skipped').length);
        setTotalAssigned(myDaily.length);
      }

      // Get bonus points
      try {
        const pointsRes = await axios.get(`${API}/prep/points`);
        const myPoints = (pointsRes.data.data || []).find(p => p.user_id === user?.id);
        setBonusPoints(myPoints?.bonus_points || 0);
      } catch (err) { console.error('Failed to fetch bonus points:', err); }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [today, user?.id, user?.member_id, user?.name, isAdmin]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSwipeRight = async (task) => {
    try {
      await axios.patch(`${API}/prep/daily/${task.id}`, {
        status: 'done',
        completed_by: user?.id,
        completed_by_name: user?.name,
      });
      setTasks(prev => prev.filter(t => t.id !== task.id));
      setDoneCount(prev => prev + 1);
      if (!task.is_mine) setBonusPoints(prev => prev + 1);
      setFlashType('success');
      setTimeout(() => setFlashType(null), 500);
      toast.success(
        task.is_mine ? 'Tâche terminée !' : 'Tâche bonus terminée ! +1 point',
        { icon: task.is_mine ? <CheckCircle2 className="text-emerald-600" size={18} /> : <Star className="text-amber-700" size={18} /> }
      );
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleSwipeLeft = async (task) => {
    try {
      await axios.patch(`${API}/prep/daily/${task.id}`, { status: 'skipped' });
      setTasks(prev => prev.filter(t => t.id !== task.id));
      setSkippedCount(prev => prev + 1);
      setFlashType('skip');
      setTimeout(() => setFlashType(null), 500);
      toast.info('Tâche passée — elle reste disponible', {
        icon: <RotateCcw className="text-orange-600" size={18} />,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const totalTasks = totalAssigned > 0 ? totalAssigned : tasks.length + doneCount;
  const progressPercent = totalTasks > 0 ? (doneCount / totalTasks) * 100 : 0;
  const myTasks = tasks.filter(t => t.is_mine);
  const otherTasks = tasks.filter(t => !t.is_mine);

  return (
    <div className={`min-h-screen p-3 md:p-8 pb-24 md:pb-8 ${flashType === 'success' ? 'success-flash' : flashType === 'skip' ? 'skip-flash' : ''}`} data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">
              Bonjour{user?.name ? `, ${user.name}` : ''} !
            </h1>
            <p className="text-gray-500">{formatDateFR(new Date())}</p>
          </div>
          <Button variant="outline" size="icon" onClick={fetchTasks} className="rounded-xl bg-white border-gray-200 text-gray-700 hover:bg-gray-100" data-testid="refresh-tasks-btn">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Stats */}
        <div className={`grid gap-3 mb-6 ${isAdmin ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-neon-pink/20 rounded-2xl p-4">
            <div className="flex items-center gap-1.5 text-neon-pink mb-1"><Users size={14} /><span className="text-xs font-bold uppercase tracking-wider">Mes tâches</span></div>
            <div className="font-heading text-3xl font-bold text-neon-pink neon-text-pink">{isAdmin ? myTasks.length : tasks.length}</div>
          </motion.div>
          {isAdmin && (
            <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-neon-yellow/20 rounded-2xl p-4">
              <div className="flex items-center gap-1.5 text-amber-700 mb-1"><Clock size={14} /><span className="text-xs font-bold uppercase tracking-wider">Disponibles</span></div>
              <div className="font-heading text-3xl font-bold text-amber-700 ">{otherTasks.length}</div>
            </motion.div>
          )}
          <motion.div whileHover={{ scale: 1.02 }} className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4">
            <div className="flex items-center gap-1.5 text-emerald-600 mb-1"><CheckCircle2 size={14} /><span className="text-xs font-bold uppercase tracking-wider">Terminées</span></div>
            <div className="font-heading text-3xl font-bold text-emerald-600">{doneCount}</div>
          </motion.div>
          <motion.div whileHover={{ scale: 1.02 }} className="bg-neon-yellow/5 border border-neon-yellow/20 rounded-2xl p-4">
            <div className="flex items-center gap-1.5 text-amber-700 mb-1"><Trophy size={14} /><span className="text-xs font-bold uppercase tracking-wider">Points bonus</span></div>
            <div className="font-heading text-3xl font-bold text-amber-700">{bonusPoints}</div>
          </motion.div>
        </div>

        {/* Progress */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Progression du jour</span>
            <span className="text-sm font-bold text-neon-pink">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-3 bg-gray-100" />
          <p className="text-xs text-gray-500 mt-1">Les tâches passées restent disponibles et ne sont pas déduites</p>
        </div>
      </div>

      {/* Kitchen tasks (assigned by the chef) */}
      <MyKitchenTasks date={tasksDate} onDateChange={setTasksDate} />

      {/* Swipe Area (mise en place quotidienne) */}
      <div className="max-w-md mx-auto mb-4">
        <h2 className="font-heading text-xl font-bold text-gray-900">Mise en place à valider</h2>
        <p className="text-gray-500 text-sm">Glissez à droite pour terminer, à gauche pour passer.</p>
      </div>
      <div className="max-w-md mx-auto relative" style={{ minHeight: '450px' }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-96">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
            <p className="text-gray-500">Chargement des tâches...</p>
          </div>
        ) : tasks.length === 0 && totalAssigned === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center h-64 text-center" data-testid="swipe-empty-none">
            <p className="text-gray-700 font-medium mb-6">Aucune tâche ne vous a été assignée pour cette journée.</p>
            <Button onClick={fetchTasks} variant="outline" className="rounded-full h-11 border-gray-300 text-gray-700" data-testid="refresh-empty-btn">
              <RefreshCw size={18} className="mr-2" />Actualiser
            </Button>
          </motion.div>
        ) : tasks.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center h-96 text-center" data-testid="swipe-empty-done">
            <div className="w-20 h-20 bg-neon-pink/10 border border-neon-pink/30 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="text-neon-pink" size={40} />
            </div>
            <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">Bravo{user?.name ? `, ${user.name}` : ''} !</h3>
            <p className="text-gray-500 mb-6">
              {isAdmin ? 'Toutes les tâches sont terminées pour aujourd\'hui.' : 'Toutes tes tâches assignées sont terminées !'}
            </p>
            <Button onClick={fetchTasks} className="bg-neon-pink hover:bg-neon-pink/90 text-gray-900 rounded-full shadow-neon-pink neon-glow-pink" data-testid="refresh-empty-btn">
              <RefreshCw size={18} className="mr-2" />Actualiser
            </Button>
          </motion.div>
        ) : (
          <AnimatePresence mode="popLayout">
            {tasks.slice(0, 3).reverse().map((task, index) => (
              <SwipeTaskCard
                key={task.id}
                task={task}
                isTop={index === tasks.slice(0, 3).length - 1}
                onSwipeRight={handleSwipeRight}
                onSwipeLeft={handleSwipeLeft}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Mobile quick buttons */}
      {!loading && tasks.length > 0 && (
        <div className="fixed bottom-28 left-1/2 -translate-x-1/2 flex gap-6 md:hidden">
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => tasks[0] && handleSwipeLeft(tasks[0])} className="w-16 h-16 bg-white shadow-lg rounded-full flex items-center justify-center border-2 border-red-500/50" data-testid="quick-skip-btn">
            <XCircle className="text-red-600" size={32} />
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => tasks[0] && handleSwipeRight(tasks[0])} className="w-16 h-16 bg-neon-pink shadow-neon-pink rounded-full flex items-center justify-center" data-testid="quick-done-btn">
            <CheckCircle2 className="text-gray-900" size={32} />
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
